// PROMPT 3.6 · Instagram Feed section (horizontal rail).
// Renders @loadout.co.in profile card + latest posts as a horizontal
// scrolling row. When the backend reports connected=false, shows a
// simple "follow us" fallback that still links to the profile.

import { useCallback, useEffect, useRef, useState } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function formatCount(n) {
        if (n == null) return "";
        if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
        if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
        return String(n);
}

function ProfileCard({ handle, profile }) {
        const url = `https://www.instagram.com/${(handle || "").replace(/^@/, "")}/`;
        const followerText = profile?.followers_count != null
                ? `${formatCount(profile.followers_count)} followers`
                : "";
        return (
                <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="instagram-profile-card"
                        className="ig-tile ig-profile"
                >
                        <div className="ig-profile-inner">
                                {/* Loadout wordmark in a dark circle (brand, never expires). */}
                                <span className="ig-avatar ig-avatar-logo" data-testid="instagram-avatar" aria-label="Loadout">
                                        <span className="font-display">LOADOUT</span>
                                </span>
                                <p className="ig-handle">@{profile?.username || (handle || "").replace(/^@/, "")}</p>
                                {followerText && (
                                        <p data-testid="instagram-follower-count" className="ig-followers">
                                                {followerText}
                                        </p>
                                )}
                                {profile?.biography && (
                                        <p className="ig-bio">{profile.biography}</p>
                                )}
                                <span className="ig-follow-btn">
                                        Follow on Instagram
                                </span>
                        </div>
                </a>
        );
}

function PostTile({ post }) {
        const src = post.media_type === "VIDEO"
                ? (post.thumbnail_url || post.media_url)
                : post.media_url;
        return (
                <a
                        href={post.permalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="instagram-post"
                        className="ig-tile ig-post"
                >
                        <div className="ig-post-frame">
                                {src ? (
                                        <img
                                                src={src}
                                                alt={(post.caption || "Instagram post").slice(0, 120)}
                                                loading="lazy"
                                                decoding="async"
                                                className="ig-post-img"
                                        />
                                ) : (
                                        <div className="ig-post-fallback" />
                                )}
                                {post.media_type === "VIDEO" && (
                                        <div className="ig-media-badge" aria-hidden="true">▶</div>
                                )}
                                {post.media_type === "CAROUSEL_ALBUM" && (
                                        <div className="ig-media-badge" aria-hidden="true">▤</div>
                                )}
                        </div>
                        {post.caption && (
                                <p className="ig-caption">{post.caption}</p>
                        )}
                </a>
        );
}

export default function InstagramFeed() {
        const [feed, setFeed] = useState(null);
        const [collabs, setCollabs] = useState([]);
        const [status, setStatus] = useState("loading");
        const scrollerRef = useRef(null);

        useEffect(() => {
                let cancelled = false;
                // Load feed (profile) and collab tiles in parallel.
                Promise.all([
                        fetch(`${BACKEND}/api/instagram/feed`).then((r) => r.ok ? r.json() : { connected: false }),
                        fetch(`${BACKEND}/api/instagram/collabs`).then((r) => r.ok ? r.json() : { collabs: [] }),
                ]).then(([f, c]) => {
                        if (cancelled) return;
                        setFeed(f);
                        setCollabs(c.collabs || []);
                        setStatus("ready");
                }).catch(() => { if (!cancelled) setStatus("error"); });
                return () => { cancelled = true; };
        }, []);

        const nudge = useCallback((dir) => {
                const el = scrollerRef.current;
                if (!el) return;
                const first = el.querySelector("[data-ig-tile]");
                const step = first ? first.getBoundingClientRect().width + 16 : el.clientWidth * 0.7;
                el.scrollBy({ left: dir * step * 1.5, behavior: "smooth" });
        }, []);

        const handle = feed?.handle || "loadout.co.in";
        const connected = feed?.connected;
        const ownPosts = feed?.posts || [];
        const profile = feed?.profile;
        const profileUrl = `https://www.instagram.com/${handle.replace(/^@/, "")}/`;
        // Prefer curated collab posts. Fall back to own posts if no collabs.
        const railPosts = collabs.length > 0
                ? collabs.map((c) => ({
                        id: c.id,
                        media_type: c.media_type,
                        media_url: `${BACKEND}${c.thumbnail_url}`,
                        thumbnail_url: `${BACKEND}${c.thumbnail_url}`,
                        permalink: c.permalink,
                        caption: c.creator_username
                                ? `@${c.creator_username}${c.caption ? " · " + c.caption : ""}`
                                : c.caption,
                        username: c.creator_username,
                }))
                : ownPosts;
        const showAny = connected || railPosts.length > 0;

        return (
                <section
                        data-testid="instagram-section"
                        className="bg-[#0A0A0A] text-white py-14 md:py-20 border-t border-white/10"
                >
                        <div className="mx-auto max-w-[1440px] px-6">
                                <p
                                        data-testid="instagram-eyebrow"
                                        className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#FF9F1C]"
                                >
                                        04 / Instagram
                                </p>
                                <h2
                                        data-testid="instagram-headline"
                                        className="mt-3 font-display leading-[0.95] uppercase ig-heading"
                                >
                                        <span className="block text-white">FOLLOW THE FEED</span>
                                        <span className="block text-[#FF9F1C]">@{handle.replace(/^@/, "")}</span>
                                </h2>
                                <p
                                        data-testid="instagram-subhead"
                                        className="mt-6 font-sans text-white/80 leading-relaxed ig-subhead"
                                        style={{ maxWidth: "56ch" }}
                                >
                                        Collabs, drop announcements, and unboxing videos — straight from the ops desk.
                                </p>

                                {status === "loading" && (
                                        <p
                                                data-testid="instagram-loading"
                                                className="mt-10 font-mono text-[11px] tracking-[0.18em] uppercase text-white/40"
                                        >
                                                Loading feed…
                                        </p>
                                )}

                                {status === "ready" && !showAny && (
                                        <div
                                                data-testid="instagram-not-connected"
                                                className="mt-8 py-16 border border-dashed border-white/15 flex items-center justify-center"
                                        >
                                                <div className="text-center max-w-md px-6">
                                                        <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-white/40">
                                                                Instagram sync not yet configured.
                                                        </p>
                                                        <a
                                                                href={profileUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                data-testid="instagram-fallback-follow"
                                                                className="mt-4 inline-block border border-[#FF9F1C] text-[#FF9F1C] hover:bg-[#FF9F1C] hover:text-black font-mono text-[12px] tracking-[0.18em] uppercase px-5 py-2 transition-colors"
                                                        >
                                                                Follow @{handle.replace(/^@/, "")}
                                                        </a>
                                                </div>
                                        </div>
                                )}

                                {status === "ready" && showAny && (
                                        <div className="mt-10 relative ig-rail-wrap">
                                                <button
                                                        type="button"
                                                        aria-label="Scroll left"
                                                        data-testid="instagram-nudge-left"
                                                        onClick={() => nudge(-1)}
                                                        className="ig-nudge ig-nudge-left"
                                                >
                                                        ‹
                                                </button>
                                                <button
                                                        type="button"
                                                        aria-label="Scroll right"
                                                        data-testid="instagram-nudge-right"
                                                        onClick={() => nudge(1)}
                                                        className="ig-nudge ig-nudge-right"
                                                >
                                                        ›
                                                </button>
                                                <div
                                                        ref={scrollerRef}
                                                        data-testid="instagram-rail"
                                                        className="ig-rail"
                                                >
                                                        <div data-ig-tile>
                                                                <ProfileCard handle={handle} profile={profile} />
                                                        </div>
                                                        {railPosts.map((p) => (
                                                                <div key={p.id} data-ig-tile>
                                                                        <PostTile post={p} />
                                                                </div>
                                                        ))}
                                                </div>
                                                <p
                                                        data-testid="instagram-scroll-hint"
                                                        className="mt-4 font-mono text-[10px] tracking-[0.16em] uppercase text-white/30"
                                                >
                                                        ← Swipe / scroll to see more →
                                                </p>
                                        </div>
                                )}
                        </div>

                        <style>{`
                                .ig-heading { font-size: 36px; }
                                .ig-subhead { font-size: 15px; }
                                @media (min-width: 481px) { .ig-heading { font-size: 40px; } .ig-subhead { font-size: 16px; } }
                                @media (min-width: 769px) { .ig-heading { font-size: 48px; } }
                                @media (min-width: 1024px) { .ig-heading { font-size: 56px; } }
                                @media (min-width: 1280px) { .ig-heading { font-size: 64px; } }

                                .ig-rail-wrap { position: relative; }
                                .ig-rail {
                                        display: flex;
                                        gap: 16px;
                                        overflow-x: auto;
                                        overflow-y: hidden;
                                        scroll-snap-type: x mandatory;
                                        -webkit-overflow-scrolling: touch;
                                        overscroll-behavior-x: contain;
                                        padding-bottom: 8px;
                                        mask-image: linear-gradient(90deg, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%);
                                        -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%);
                                }
                                .ig-rail::-webkit-scrollbar { height: 6px; }
                                .ig-rail::-webkit-scrollbar-track { background: transparent; }
                                .ig-rail::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.14); border-radius: 3px; }
                                .ig-rail::-webkit-scrollbar-thumb:hover { background: #FF9F1C; }

                                .ig-tile {
                                        display: block;
                                        width: 260px;
                                        flex: 0 0 auto;
                                        scroll-snap-align: start;
                                        border: 1px solid rgba(255,255,255,0.1);
                                        background: #0F0F0F;
                                        color: white;
                                        text-decoration: none;
                                        transition: border-color 200ms ease;
                                }
                                .ig-tile:hover { border-color: #FF9F1C; }
                                @media (min-width: 481px) { .ig-tile { width: 280px; } }
                                @media (min-width: 769px) { .ig-tile { width: 300px; } }
                                @media (min-width: 1024px) { .ig-tile { width: 320px; } }

                                .ig-profile-inner {
                                        padding: 24px;
                                        display: flex;
                                        flex-direction: column;
                                        align-items: flex-start;
                                        gap: 12px;
                                        min-height: 100%;
                                }
                                .ig-avatar {
                                        width: 64px;
                                        height: 64px;
                                        border-radius: 999px;
                                        object-fit: cover;
                                        background: #1a1a1a;
                                }
                                .ig-avatar-logo {
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        background: #0A0A0A;
                                        border: 1px solid rgba(255,159,28,0.6);
                                        overflow: hidden;
                                }
                                .ig-avatar-logo span { font-size: 12px; line-height: 1; letter-spacing: 0.02em; color: #FFFFFF; }
                                .ig-avatar-fallback {
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        color: #FF9F1C;
                                }
                                .ig-handle {
                                        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
                                        font-size: 14px;
                                        letter-spacing: 0.06em;
                                        color: white;
                                }
                                .ig-followers {
                                        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
                                        font-size: 20px;
                                        line-height: 1;
                                        color: #FF9F1C;
                                        margin-top: 4px;
                                }
                                .ig-bio {
                                        font-family: system-ui, -apple-system, sans-serif;
                                        font-size: 12px;
                                        color: rgba(255,255,255,0.6);
                                        line-height: 1.4;
                                        display: -webkit-box;
                                        -webkit-line-clamp: 3;
                                        -webkit-box-orient: vertical;
                                        overflow: hidden;
                                }
                                .ig-follow-btn {
                                        margin-top: auto;
                                        display: inline-block;
                                        border: 1px solid #FF9F1C;
                                        color: #FF9F1C;
                                        padding: 8px 12px;
                                        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
                                        font-size: 11px;
                                        letter-spacing: 0.14em;
                                        text-transform: uppercase;
                                }

                                .ig-post-frame {
                                        position: relative;
                                        aspect-ratio: 1 / 1;
                                        background: #111;
                                        overflow: hidden;
                                }
                                .ig-post-img {
                                        width: 100%;
                                        height: 100%;
                                        object-fit: cover;
                                        transition: transform 300ms ease;
                                }
                                .ig-tile:hover .ig-post-img { transform: scale(1.02); }
                                .ig-post-fallback { width: 100%; height: 100%; background: #111; }
                                .ig-media-badge {
                                        position: absolute;
                                        top: 8px;
                                        right: 8px;
                                        background: rgba(0,0,0,0.55);
                                        color: white;
                                        padding: 3px 8px;
                                        font-size: 12px;
                                        line-height: 1;
                                }
                                .ig-caption {
                                        padding: 10px 12px 14px;
                                        font-family: system-ui, -apple-system, sans-serif;
                                        font-size: 12px;
                                        color: rgba(255,255,255,0.7);
                                        line-height: 1.4;
                                        display: -webkit-box;
                                        -webkit-line-clamp: 2;
                                        -webkit-box-orient: vertical;
                                        overflow: hidden;
                                }

                                .ig-nudge {
                                        position: absolute;
                                        top: 50%;
                                        transform: translateY(-50%);
                                        z-index: 5;
                                        width: 44px;
                                        height: 44px;
                                        display: none;
                                        align-items: center;
                                        justify-content: center;
                                        background: rgba(10,10,10,0.85);
                                        color: white;
                                        font-size: 28px;
                                        line-height: 1;
                                        border: 1px solid rgba(255,255,255,0.14);
                                        cursor: pointer;
                                        transition: border-color 200ms ease, background 200ms ease;
                                }
                                .ig-nudge:hover { border-color: #FF9F1C; background: rgba(255,159,28,0.15); }
                                .ig-nudge-left { left: -22px; }
                                .ig-nudge-right { right: -22px; }
                                @media (hover: hover) and (pointer: fine) {
                                        .ig-nudge { display: flex; }
                                }

                                @media (prefers-reduced-motion: reduce) {
                                        .ig-rail { scroll-behavior: auto; }
                                        .ig-post-img { transition: none !important; transform: none !important; }
                                }
                        `}</style>
                </section>
        );
}
